import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Lottie from 'lottie-react';
import { useNavigate, useLocation } from 'react-router-dom';
import WaitImg from '../assets/wait.json';
import Spacer from '../components/Spacer';
import useFormStore from "../state/useFormStore";
import { apiService } from '../utils/api';

const Wrapper = styled.div`
  height: 100vh;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin: 0 36px;
  margin-top: 20px;
  padding-bottom: 100px;
`;

const TitleText = styled.h2`
  font-size: 20px;
  line-height: 24px;
  margin: 20px 0;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.basic1000};
`;

const MainText = styled.div`
  margin: 10px 0 20px 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.basic2000};
`;

const Subtext = styled.h4`
  font-size: 20px;
  line-height: 26px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.basic2000};
  margin: 5px 0 40px 0;
  text-align: center;
`;

const Busqueda = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);

  const hashFromUrl = window.location.pathname.split('/')[1];
  const hashFromLocalStorage = localStorage.getItem('hash');
  const hash = useFormStore(state => state.hash) || hashFromLocalStorage || hashFromUrl;

  const { email, phone, requestUid, setOffer } = useFormStore(state => ({
    email: state.email,
    phone: state.phone,
    requestUid: state.requestUid,
    setOffer: state.setOffer
  }));

  const authenticateAndFetchOffers = async () => {
    try {
      if (!requestUid) {
        throw new Error("No hay requestUid disponible");
      }

      let intervalId: NodeJS.Timeout | null = null;
      let maxTime = 50000; // 50 segundos máximo
      let interval = 5000; // Consultar cada 5 segundos
      let currentElapsedTime = 0; // Variable local para mayor control
      
      const fetchOffers = async () => {
        try {
          const response = await apiService.requests.getOffers(requestUid);
          
          // Si hay respuesta exitosa con finalOffers
          if (response.data?.data?.finalOffers) {
            const finalOffers = response.data.data.finalOffers;
            const canContinue = response.data?.requestStatus?.statusDetail?.canContinue;

            if (finalOffers.length > 0) {
              // Hay ofertas disponibles
              setOffer(finalOffers);
              console.log("Ofertas guardadas en Zustand:", finalOffers);
              
              if (intervalId) {
                clearInterval(intervalId);
              }
              
              navigate(`/${hash}/oferta`);
              return true;
            } else if (!canContinue) {
              // No hay ofertas y no se puede continuar
              console.log("No hay ofertas disponibles y no se puede continuar.");
              
              if (intervalId) {
                clearInterval(intervalId);
              }
              
              navigate(`/${hash}/rechazado`);
              return true;
            }
            
            // Si llegamos aquí, no hay ofertas pero podemos continuar
            console.log(`No hay ofertas aún. Reintentando... (${currentElapsedTime/1000}s)`);
            return false;
          } else {
            throw new Error("Respuesta de API inválida");
          }
        } catch (error: any) {
          // Verificar si es el error específico OFFERS001 con el nuevo formato
          if (error?.response?.data?.error?.code === "OFFERS001") {
            console.log("Error OFFERS001: No hay ofertas disponibles");
            
            // Verificar canContinue del nuevo formato de respuesta
            const canContinue = error?.response?.data?.requestStatus?.statusDetail?.canContinue;
            
            if (canContinue === false) {
              console.log("No se puede continuar según la respuesta de error.");
              
              if (intervalId) {
                clearInterval(intervalId);
              }
              
              navigate(`/${hash}/rechazado`);
              return true;
            }
            
            // Si canContinue es true y no hemos excedido el tiempo
            if (currentElapsedTime < maxTime) {
              console.log(`Reintentando (${currentElapsedTime/1000}s)...`);
              return false; // Seguir con los reintentos
            } else {
              // Se acabó el tiempo, navegar a rechazado
              console.log("Tiempo máximo alcanzado sin obtener ofertas.");
              
              if (intervalId) {
                clearInterval(intervalId);
              }
              
              navigate(`/${hash}/rechazado`);
              return true;
            }
          } else {
            // Cualquier otro error
            console.error("Error al obtener ofertas:", error);
            
            if (intervalId) {
              clearInterval(intervalId);
            }
            
            navigate(`/${hash}/rechazado`);
            return true;
          }
        }
      };

      // Primera ejecución inmediata
      const initialResult = await fetchOffers();
      
      // Si la primera ejecución no encontró ofertas, iniciar el bucle
      if (!initialResult) {
        intervalId = setInterval(async () => {
          currentElapsedTime += interval;
          setElapsedTime(currentElapsedTime);
          
          console.log(`Intento de obtener ofertas: ${currentElapsedTime/1000}s de ${maxTime/1000}s`);
          
          // Si ya alcanzamos el tiempo máximo
          if (currentElapsedTime >= maxTime) {
            console.log("Alcanzado tiempo máximo. Finalizando reintentos.");
            
            if (intervalId) {
              clearInterval(intervalId);
              // Hacer la última consulta y forzar navegación a rechazado
              try {
                await fetchOffers();
              } finally {
                navigate(`/${hash}/rechazado`);
              }
            }
            return;
          }
          
          // Ejecutar consulta normal dentro del intervalo
          await fetchOffers();
        }, interval);
      }

    } catch (error) {
      console.error("Error general:", error);
      navigate(`/${hash}/rechazado`);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0 });
    let timeoutId: NodeJS.Timeout | null = null;
    
    authenticateAndFetchOffers();
    
    // Garantizar navegación después de tiempo máximo en caso de problemas
    timeoutId = setTimeout(() => {
      console.log("Tiempo máximo total alcanzado: navegando a rechazado");
      navigate(`/${hash}/rechazado`);
    }, 52000); // Damos un poco más del tiempo máximo de reintentos
    
    return () => {
      setLoading(false);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Wrapper>
      <TitleText style={{ fontWeight: '800' }}>Buscando más ofertas...</TitleText>
      <Lottie animationData={WaitImg} height={150} width={150} style={{ width: '200px' }} />
      <MainText>¡Gracias por elegirnos!</MainText>
      <Subtext>¡Estamos buscando las mejores ofertas! Esto puede tardar algunos segundos...</Subtext>
      <Spacer size={30} />
    </Wrapper>
  );
};

export default Busqueda;